import React, { useState } from 'react';
import { Loader2, FileText, MapPin, Calendar } from 'lucide-react';
import { todayYMD } from '../utils/dateHelpers';

/**
 * QuoteRequestForm — lightweight quote request used for subcontractors /
 * price-work / teams / limited-company tradespeople. The collected data is
 * posted as the first structured chat message rather than a day-rate offer.
 */
export default function QuoteRequestForm({ onSubmit, onCancel, loading = false }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    target_start_date: '',
  });
  const [errors, setErrors] = useState({});

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim() || form.title.trim().length < 4) next.title = 'Add a short job title (min 4 chars)';
    if (!form.description.trim() || form.description.trim().length < 10) next.description = 'Describe the work (min 10 chars)';
    if (!form.location.trim()) next.location = 'Add a town, area or postcode';
    if (!form.target_start_date) next.target_start_date = 'Pick a target start date';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(form);
  };

  const today = todayYMD();

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-request-form">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800 flex gap-2">
        <FileText className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          Share the basics so the tradesperson can come back to you with a price. You can agree final
          numbers in chat — no day rate is locked in here.
        </span>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Job title <span className="text-rose-500">*</span>
        </label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => update('title', e.target.value)}
          maxLength={100}
          placeholder="e.g. Kitchen rewire, Loft insulation, Bathroom fit-out"
          className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
          data-testid="quote-title-input"
        />
        {errors.title && <p className="text-xs text-rose-600 mt-1">{errors.title}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Describe the work <span className="text-rose-500">*</span>
        </label>
        <textarea
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          maxLength={1000}
          rows={4}
          placeholder="Scope, materials, timescales, access constraints, anything relevant to quote accurately."
          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
          data-testid="quote-description-input"
        />
        <p className="text-xs text-slate-400 mt-1">{form.description.length}/1000</p>
        {errors.description && <p className="text-xs text-rose-600 mt-1">{errors.description}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1">
            <MapPin className="h-4 w-4 text-slate-400" />
            Location / postcode <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => update('location', e.target.value)}
            maxLength={100}
            placeholder="e.g. SW11 or Clapham"
            className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
            data-testid="quote-location-input"
          />
          {errors.location && <p className="text-xs text-rose-600 mt-1">{errors.location}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1">
            <Calendar className="h-4 w-4 text-slate-400" />
            Target start date <span className="text-rose-500">*</span>
          </label>
          <input
            type="date"
            value={form.target_start_date}
            onChange={(e) => update('target_start_date', e.target.value)}
            min={today}
            className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
            data-testid="quote-target-date-input"
          />
          {errors.target_start_date && <p className="text-xs text-rose-600 mt-1">{errors.target_start_date}</p>}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="btn-secondary flex-1"
          data-testid="quote-cancel-btn"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="btn-primary flex-1 flex items-center justify-center gap-2"
          data-testid="quote-submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            'Send quote request'
          )}
        </button>
      </div>
    </form>
  );
}
